/**
 * MCP Client Adapter
 *
 * Consume any MCP server as agent tools.
 * Google ADK / OpenAI / Vercel / Claude pattern.
 */

import { z } from 'zod';
import type { Tool, ToolContext } from '../core/types';

export interface MCPServerConfig {
  /** MCP server URL or transport */
  url: string;
  /** Server name */
  name: string;
  /** Authentication headers */
  headers?: Record<string, string>;
  /** Tool filter (only expose these tools) */
  toolFilter?: string[];
  /** Timeout for tool calls */
  timeoutMs?: number;
}

export interface MCPToolDefinition {
  name: string;
  description: string;
  inputSchema: Record<string, unknown>;
}

/**
 * MCP Client that discovers and wraps MCP server tools as EADK tools.
 */
export class MCPClient {
  private config: MCPServerConfig;
  private tools: Map<string, MCPToolDefinition> = new Map();

  constructor(config: MCPServerConfig) {
    this.config = config;
  }

  /**
   * Discover available tools from the MCP server.
   */
  async discover(): Promise<MCPToolDefinition[]> {
    try {
      const response = await fetch(`${this.config.url}/tools/list`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(this.config.headers ?? {}),
        },
        body: JSON.stringify({ jsonrpc: '2.0', method: 'tools/list', id: 1 }),
      });

      if (!response.ok) return [];

      const data = (await response.json()) as {
        result?: { tools?: MCPToolDefinition[] };
      };
      const serverTools = data.result?.tools ?? [];

      // Filter if configured
      const filtered = this.config.toolFilter
        ? serverTools.filter((t) => this.config.toolFilter!.includes(t.name))
        : serverTools;

      for (const tool of filtered) {
        this.tools.set(tool.name, tool);
      }

      return filtered;
    } catch {
      return [];
    }
  }

  /**
   * Get EADK tools from discovered MCP tools.
   */
  getTools(): Tool[] {
    return [...this.tools.values()].map((mcpTool) => this.wrapMCPTool(mcpTool));
  }

  /**
   * Call a specific MCP tool.
   */
  async callTool(name: string, args: unknown): Promise<unknown> {
    const response = await fetch(`${this.config.url}/tools/call`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(this.config.headers ?? {}),
      },
      body: JSON.stringify({
        jsonrpc: '2.0',
        method: 'tools/call',
        params: { name, arguments: args },
        id: Date.now(),
      }),
    });

    if (!response.ok) {
      throw new Error(`MCP tool call failed: ${response.status}`);
    }

    const data = (await response.json()) as {
      result?: { content?: Array<{ text?: string }> };
    };
    return data.result?.content?.[0]?.text ?? data.result;
  }

  private wrapMCPTool(mcpTool: MCPToolDefinition): Tool {
    return {
      name: `${this.config.name}__${mcpTool.name}`,
      description: mcpTool.description,
      inputSchema: z.any(),
      execute: async (input: unknown, _ctx: ToolContext) => {
        return this.callTool(mcpTool.name, input);
      },
      computeTarget: 'cloud',
    };
  }
}
